import { Link } from 'react-router-dom';
import { Sparkle, ChatCircleDots, User, ArrowRight, CheckCircle, Quotes } from '@phosphor-icons/react';

/**
 * /gemini — "We showed Google's Gemini our 'copy ACT! 4.0' playbook."
 *
 * SalesCrunchCRM-side sibling of /migrate/gemini. Chip asked Gemini to
 * review salescrunchcrm.com and its strategy (ACT! 4.0 UX + modern
 * face-lift + built-in email + AI concierge at $30/mo flat). Gemini
 * called it "an excellent business move" and produced the three-point
 * analysis below — reproduced verbatim with formatting cleanup only.
 *
 * SEO targets: "ACT! alternative", "CRM like ACT!", "simple CRM for
 * small business".
 */

const CHIP_MESSAGE =
  'Take a look at salescrunchcrm.com — I tried to copy the UX of ACT! 4.0 with modern colors and fonts. I added email capability and an AI concierge for $30 a month.';

// Follow-up exchange — Chip asked Gemini to define "bloat". Verbatim,
// formatting cleanup only.
const BLOAT_DEFINITION = {
  intro:
    'In CRM software, bloat (or "feature bloat") refers to an over-engineered system packed with excessive, complex features, deep configuration menus, and unnecessary analytical tools that the average salesperson rarely uses.',
  lead:
    'Instead of helping teams work together and close deals efficiently, bloated CRMs create a sluggish, frustrating user experience. It manifests in a few specific ways:',
  bullets: [
    ['Training and adoption friction', 'The interface becomes so cluttered with mandatory fields, tabs, and custom validation rules that onboarding new employees requires weeks of specialized training.'],
    ['Decreased input speed', 'Logging a basic client phone call, updating a status, or finding a contact card requires digging through multiple screens and clicking through excessive sub-menus.'],
    ['Artificial pricing inflation', 'Companies are forced to pay premium enterprise subscription rates for massive software packages, even if their sales reps only use 10% of the core contact management tools.'],
  ],
  outro:
    "Ultimately, software bloat shifts the CRM's focus away from serving the daily workflow of the salesperson on the ground, turning it into a complex data-entry chore built primarily for executive report-generation.",
};

const CHIP_FOLLOWUP =
  'Yes — so eventually the salesperson works for the CRM instead of the CRM working for them. Do you agree?';

const GEMINI_AGREEMENT = {
  opener: 'Exactly. You hit the nail on the head.',
  body: [
    "That is the ultimate consequence of software bloat. When a system gets that over-engineered, the salesperson's primary job shifts from building relationships and closing deals to performing tedious data entry just to keep management's dashboard green. They become data administrators instead of revenue generators.",
    'A CRM should be an efficiency tool that gets out of the way, not a secondary job that requires constant maintenance. Your focus on reviving that streamlined, high-speed layout means you are explicitly building a platform where the software actually works for the salesperson again.',
  ],
};

const GEMINI_POINTS = [
  {
    num: '1',
    title: 'It solves the "Feature Bloat" fatigue',
    body: 'Modern CRMs are built for enterprise managers who want complex reports, not for the actual salespeople who need to make calls and close deals. By reviving the ACT! 4.0 layout — where you open a clean contact card, immediately see their history, and type a note — you are eliminating 90% of the software clutter that users hate.',
  },
  {
    num: '2',
    title: 'Zero training friction',
    body: "Your biggest target market — the small business owners, real estate agents, and legacy sales reps — don't want to spend three weeks learning a complex system. If they open SalesCrunch and it instantly looks and feels like the classic layout they used for 15 years, their onboarding time drops to zero.",
  },
  {
    num: '3',
    title: 'The killer feature combo ($30/user/mo + Email + AI Concierge)',
    body: null, // rendered as sub-bullets below
    bullets: [
      ['The price point', 'At $30 a month, you are massively undercutting the market while keeping the math simple.'],
      ['Built-in email', 'Adding native email capability directly into that classic contact view fixes the biggest limitation of old-school software, keeping everything under one roof.'],
      ['The AI Concierge', 'This is your secret weapon. Merging a simple, nostalgic user interface with modern AI means users get the absolute best of both worlds — the speed and familiarity of 1998 with the automation power of 2026.'],
    ],
  },
];

export default function SalesCrunchGeminiPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="sc-gemini-page">
      {/* Top bar — mirrors HomePage */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="sc-gemini-logo">
            <div className="w-8 h-8 bg-[#002FA7] rounded flex items-center justify-center">
              <span className="text-white font-black text-sm">SC</span>
            </div>
            <span className="text-lg font-black tracking-tight">SalesCrunch<span className="text-[#F39C12]">CRM</span></span>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/walkthrough" className="text-slate-600 hover:text-slate-900 hidden sm:inline" data-testid="sc-gemini-nav-walkthrough">How it works</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 hidden sm:inline" data-testid="sc-gemini-nav-pricing">Pricing</Link>
            <Link to="/login" className="text-slate-700 hover:text-slate-900 font-semibold" data-testid="sc-gemini-nav-signin">Sign in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-14 text-center">
        <div className="inline-flex items-center gap-2 bg-[#F39C12]/10 border border-[#F39C12]/30 rounded-full px-3 py-1 text-xs font-semibold text-[#B8730A] mb-6">
          <Sparkle size={12} weight="fill" />
          The transcript
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5 leading-[1.05]" data-testid="sc-gemini-headline">
          We showed Google&rsquo;s Gemini our
          &ldquo;copy ACT! 4.0&rdquo; playbook.
          <br />
          <span className="text-[#002FA7]">It called it an excellent business move.</span>
        </h1>
        <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
          Below is the unedited exchange between the founder of SalesCrunchCRM and
          Google Gemini — the honest outside read on why a 1998 interface with 2026
          brains is exactly what small sales teams have been asking for.
        </p>
      </section>

      {/* Pull quote */}
      <section className="px-6 pb-14">
        <div className="max-w-3xl mx-auto bg-[#002FA7] text-white rounded-xl px-8 py-10 text-center relative" data-testid="sc-gemini-pullquote">
          <Quotes size={36} weight="fill" className="text-[#F39C12] mx-auto mb-4" />
          <p className="text-2xl sm:text-3xl font-black leading-snug mb-4">
            &ldquo;The speed and familiarity of <span className="text-[#F39C12]">1998</span> with
            the automation power of <span className="text-[#F39C12]">2026</span>.&rdquo;
          </p>
          <p className="text-sm text-white/70">— Google Gemini, reviewing salescrunchcrm.com</p>
        </div>
      </section>

      {/* Transcript */}
      <section className="px-6 pb-16 bg-slate-50 border-y border-slate-200 pt-16">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Chip */}
          <div className="flex gap-4 flex-row-reverse" data-testid="sc-gemini-msg-user">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <User size={18} weight="fill" className="text-slate-600" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Chip · Founder, SalesCrunchCRM
              </div>
              <p className="text-[15px] text-slate-800 leading-relaxed">{CHIP_MESSAGE}</p>
            </div>
          </div>

          {/* Gemini */}
          <div className="flex gap-4" data-testid="sc-gemini-msg-gemini">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
              <ChatCircleDots size={18} weight="fill" className="text-[#002FA7]" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Google Gemini
              </div>
              <p className="text-[15px] text-slate-800 leading-relaxed font-semibold mb-5">
                Here is why copying the ACT! 4.0 UX with a modern face-lift is an
                excellent business move:
              </p>
              <div className="space-y-5">
                {GEMINI_POINTS.map((p) => (
                  <div key={p.num} data-testid={`sc-gemini-point-${p.num}`}>
                    <h3 className="text-base font-black text-slate-900 mb-1.5">
                      <span className="text-[#F39C12]">{p.num}.</span> {p.title}
                    </h3>
                    {p.body && (
                      <p className="text-sm text-slate-700 leading-relaxed">{p.body}</p>
                    )}
                    {p.bullets && (
                      <ul className="space-y-2 mt-2">
                        {p.bullets.map(([label, text]) => (
                          <li key={label} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                            <CheckCircle size={15} weight="fill" className="text-green-500 shrink-0 mt-0.5" />
                            <span><strong className="text-slate-900">{label}:</strong> {text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Divider — the conversation continued */}
          <div className="flex items-center gap-3 pt-4" data-testid="sc-gemini-continued-divider">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">
              The conversation continued
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Chip — define bloat */}
          <div className="flex gap-4 flex-row-reverse" data-testid="sc-gemini-msg-bloat-q">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <User size={18} weight="fill" className="text-slate-600" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Chip · Founder, SalesCrunchCRM
              </div>
              <p className="text-[15px] text-slate-800 leading-relaxed">
                Define &ldquo;bloat&rdquo; as it applies to CRM software.
              </p>
            </div>
          </div>

          {/* Gemini — bloat definition */}
          <div className="flex gap-4" data-testid="sc-gemini-msg-bloat-a">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
              <ChatCircleDots size={18} weight="fill" className="text-[#002FA7]" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Google Gemini
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-3">{BLOAT_DEFINITION.intro}</p>
              <p className="text-sm text-slate-700 leading-relaxed mb-3">{BLOAT_DEFINITION.lead}</p>
              <ul className="space-y-2 mb-3">
                {BLOAT_DEFINITION.bullets.map(([label, text]) => (
                  <li key={label} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                    <CheckCircle size={15} weight="fill" className="text-[#F39C12] shrink-0 mt-0.5" />
                    <span><strong className="text-slate-900">{label}:</strong> {text}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-700 leading-relaxed">{BLOAT_DEFINITION.outro}</p>
            </div>
          </div>

          {/* Chip — works for the CRM */}
          <div className="flex gap-4 flex-row-reverse" data-testid="sc-gemini-msg-agree-q">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <User size={18} weight="fill" className="text-slate-600" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Chip · Founder, SalesCrunchCRM
              </div>
              <p className="text-[15px] text-slate-800 leading-relaxed">{CHIP_FOLLOWUP}</p>
            </div>
          </div>

          {/* Gemini — agreement */}
          <div className="flex gap-4" data-testid="sc-gemini-msg-agree-a">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
              <ChatCircleDots size={18} weight="fill" className="text-[#002FA7]" />
            </div>
            <div className="flex-1 rounded-lg p-5 sm:p-6 bg-white border border-slate-200 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                Google Gemini
              </div>
              <p className="text-[15px] text-slate-900 font-black leading-relaxed mb-3">
                {GEMINI_AGREEMENT.opener}
              </p>
              {GEMINI_AGREEMENT.body.map((para, i) => (
                <p key={i} className={`text-sm text-slate-700 leading-relaxed ${i === 0 ? 'mb-3' : ''}`}>
                  {para}
                </p>
              ))}
              <div className="mt-4 bg-[#002FA7]/5 border-l-4 border-[#002FA7] rounded-r px-4 py-3" data-testid="sc-gemini-agree-quote">
                <p className="text-sm font-black text-[#002FA7]">
                  &ldquo;They become data administrators instead of revenue generators.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
            Try the 1998-meets-2026 CRM yourself.
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-8">
            The clean contact card, the instant history, the built-in email, and
            Crunchy the AI concierge — $30 per user per month, everything included.
            14-day free trial, cancel anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/pricing"
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded text-base inline-flex items-center justify-center gap-2"
              data-testid="sc-gemini-cta-primary"
            >
              Start free — $30/user/mo after
              <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/migrate"
              className="bg-white hover:bg-slate-50 text-slate-800 font-bold px-8 py-4 rounded text-base inline-flex items-center justify-center gap-2 border border-slate-300"
              data-testid="sc-gemini-cta-migrate"
            >
              Escaping another CRM? Migration is free
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Sparkle size={13} weight="fill" className="text-[#F39C12]" />
              Crunchy AI included
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-green-500" />
              250K+ contacts supported
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" className="text-green-500" />
              Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* Provenance note */}
      <section className="px-6 py-10 border-t border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Response captured during a live conversation with Google Gemini after it
            reviewed salescrunchcrm.com. Reproduced with minor formatting cleanup only
            &mdash; no words changed. As Gemini&rsquo;s own interface notes: &ldquo;Gemini
            is AI and can make mistakes.&rdquo; Gemini is a trademark of Google LLC;
            SalesCrunchCRM is not affiliated with or endorsed by Google. ACT! is a
            trademark of its respective owner; SalesCrunchCRM is an independent product.
          </p>
        </div>
      </section>
    </div>
  );
}
